import { SmartVideoDraftPayload } from '../../../core/services/voice-actions/models';
import { SmartVideoRunOptions } from '../models/video-generator.models';

export function shouldRetainSmartVideoDraftAfterApply(
  draft: Pick<SmartVideoDraftPayload, 'source'> | null | undefined
): boolean {
  return draft?.source === 'jivu-command';
}

export function resolveSmartVideoDraftSource(
  action: { payload?: Record<string, unknown> } | null | undefined
): SmartVideoDraftPayload['source'] {
  const source = action?.payload?.['source'];
  return source === 'jivu-command' || source === 'trend-radar'
    ? source
    : 'voice-assistant';
}

export function mergeSmartVideoDraftRunOptions(
  base: SmartVideoRunOptions,
  draft: SmartVideoDraftPayload
): SmartVideoRunOptions {
  const merged: SmartVideoRunOptions = { ...base };

  copyDefined(draft, merged, 'autoReview');
  copyDefined(draft, merged, 'durationSeconds');
  copyDefined(draft, merged, 'scriptSystemPrompt');
  copyDefined(draft, merged, 'scriptTemperature');
  copyDefined(draft, merged, 'imageStyle');
  copyDefined(draft, merged, 'imageStylePromptEnhancement');
  copyDefined(draft, merged, 'useTts');
  copyDefined(draft, merged, 'useBrowserTts');
  copyDefined(draft, merged, 'browserTtsVoiceUri');
  copyDefined(draft, merged, 'ttsVoiceId');
  copyDefined(draft, merged, 'useAiVideoGeneration');
  copyDefined(draft, merged, 'aiVideoMode');
  copyDefined(draft, merged, 'aiVideoIntensity');
  copyDefined(draft, merged, 'aspectRatio');
  copyDefined(draft, merged, 'imageResolution');
  copyDefined(draft, merged, 'imageAspectRatio');
  copyDefined(draft, merged, 'imageQuality');
  copyDefined(draft, merged, 'imageOutputFormat');
  copyDefined(draft, merged, 'videoResolution');
  copyDefined(draft, merged, 'videoAspectRatio');
  copyDefined(draft, merged, 'videoQuality');
  copyDefined(draft, merged, 'videoDurationSeconds');
  copyDefined(draft, merged, 'videoOutputFormat');
  copyDefined(draft, merged, 'generateThumbnails');
  copyDefined(draft, merged, 'generateShorts');
  copyDefined(draft, merged, 'shortsCount');
  copyDefined(draft, merged, 'selectedSceneIndexes');
  copyDefined(draft, merged, 'scriptProvider');
  copyDefined(draft, merged, 'scriptModel');
  copyDefined(draft, merged, 'imageProvider');
  copyDefined(draft, merged, 'imageModel');
  copyDefined(draft, merged, 'ttsProvider');
  copyDefined(draft, merged, 'ttsModel');
  copyDefined(draft, merged, 'videoProvider');
  copyDefined(draft, merged, 'videoModel');
  copyDefined(draft, merged, 'renderOutput');
  copyDefined(draft, merged, 'featuredCharacterIds');
  copyDefined(draft, merged, 'featuredEnvironmentIds');
  copyDefined(draft, merged, 'featuredProductIds');
  copyDefined(draft, merged, 'enableCharacterDialogue');
  copyDefined(draft, merged, 'dialogueTtsMode');

  const explicitSceneCount = draft.sceneCount === undefined
    ? undefined
    : clampInt(draft.sceneCount, 1, 160, merged.sceneCount ?? merged.aiImageCount ?? 8);
  const aiImageCount = draft.aiImageCount === undefined
    ? (explicitSceneCount === undefined ? merged.aiImageCount : clampInt(explicitSceneCount, 1, 80, merged.aiImageCount ?? 8))
    : clampInt(draft.aiImageCount, 1, 80, merged.aiImageCount ?? explicitSceneCount ?? 8);
  const maxAiVideoCount = Math.max(0, aiImageCount ?? explicitSceneCount ?? merged.sceneCount ?? 80);
  const aiVideoCount = draft.aiVideoCount === undefined
    ? (draft.useAiVideoGeneration === false ? 0 : merged.aiVideoCount)
    : clampInt(draft.aiVideoCount, 0, maxAiVideoCount, merged.aiVideoCount ?? 0);

  merged.aiImageCount = aiImageCount;
  merged.aiVideoCount = aiVideoCount;
  if (explicitSceneCount !== undefined) {
    merged.sceneCount = explicitSceneCount;
  } else if (aiImageCount !== undefined) {
    merged.sceneCount = aiImageCount;
  }

  if ((merged.aiVideoCount ?? 0) === 0) {
    merged.useAiVideoGeneration = false;
  } else {
    merged.useAiVideoGeneration = true;
  }

  return merged;
}

function copyDefined<K extends keyof SmartVideoRunOptions>(
  source: Partial<Record<K, SmartVideoRunOptions[K]>>,
  target: SmartVideoRunOptions,
  key: K
): void {
  const value = source[key];
  if (value !== undefined) {
    target[key] = value;
  }
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(numeric)));
}
